import type { FormInputComponent } from '../types'
import type { FilteredComponent, PreviewRoleType, RolePermission } from '../types/preview'
import { generateMockReports } from './previewMockData'

interface PermissionTemplateFixedRole {
	id: string
	permissionTemplateId: string
	fixedRole: string
	write: boolean
	read: boolean
	optional: boolean
	reports: boolean
	deleted: boolean
}

interface PermissionTemplate {
	id: string
	name: string
	fixedRoles: PermissionTemplateFixedRole[]
}

/**
 * Get permission flags for a specific role from a permission template
 */
export function getFixedRolePermission(
	template: PermissionTemplate | undefined,
	role: PreviewRoleType,
): RolePermission {
	if (!template || !template.fixedRoles) {
		return { read: true, write: true, reports: false, optional: false }
	}

	const fixedRole = template.fixedRoles.find((fr) => fr.fixedRole === role && !fr.deleted)

	if (!fixedRole) {
		return { read: false, write: false, reports: false, optional: false }
	}

	return {
		read: fixedRole.read,
		write: fixedRole.write,
		reports: fixedRole.reports,
		optional: fixedRole.optional,
	}
}

/**
 * Filter form components based on role permissions
 */
export function filterComponentsByRole(
	components: FormInputComponent[],
	permissionTemplates: PermissionTemplate[],
	selectedRole: PreviewRoleType,
): FilteredComponent[] {
	const templateMap = new Map(permissionTemplates.map((t) => [t.id, t]))

	const filterRecursive = (comps: FormInputComponent[]): FilteredComponent[] => {
		return comps
			.filter((comp) => {
				// Skip deleted components
				if (comp.action === 'delete') return false

				const template = templateMap.get(comp.permissionTemplateId)
				const permission = getFixedRolePermission(template, selectedRole)

				// Component is visible if role has read, write, or reports permission
				return permission.read || permission.write || permission.reports
			})
			.map((comp) => {
				const template = templateMap.get(comp.permissionTemplateId)
				const permission = getFixedRolePermission(template, selectedRole)

				// Determine if this is report-only (can see reports but not write)
				const reportOnly = !permission.write && permission.reports

				// Generate mock reports if this role can see reports for input components
				const shouldGenerateMockReports = permission.reports && isInputComponent(comp.name)

				const filteredComponent: FilteredComponent = {
					...comp,
					read: permission.read,
					write: permission.write,
					reports: permission.reports,
					optional: permission.optional,
					reportOnly,
					mockReports: shouldGenerateMockReports ? generateMockReports(comp, selectedRole) : undefined,
					children: comp.children ? filterRecursive(comp.children) : [],
				}

				return filteredComponent
			})
	}

	return filterRecursive(components)
}

/**
 * Check if a component type is an input component (can have reports)
 */
function isInputComponent(componentName: string): boolean {
	const inputComponents = ['rating', 'text', 'textarea', 'select', 'number', 'checkbox', '9boxrating']
	return inputComponents.includes(componentName)
}

/**
 * Get all unique permission template IDs from components
 */
export function getUniquePermissionTemplateIds(components: FormInputComponent[]): Set<string> {
	const ids = new Set<string>()

	const collectIds = (comps: FormInputComponent[]) => {
		for (const comp of comps) {
			if (comp.permissionTemplateId && comp.action !== 'delete') {
				ids.add(comp.permissionTemplateId)
			}
			if (comp.children) {
				collectIds(comp.children)
			}
		}
	}

	collectIds(components)
	return ids
}
